import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  makeStyles,
  IconButton,
} from "@material-ui/core";
import { ExpandMore as ExpandMoreIcon, Close as CloseIcon } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    background: "linear-gradient(to right, #182229, #202d37)",
    color: "#ffffff",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
  },
  dialogTitleText: {
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "1.15rem",
  },
  dialogContent: {
    padding: "16px 24px",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  },
  dialogActions: {
    padding: "12px 24px",
    borderTop: "1px solid rgba(0, 0, 0, 0.1)",
    backgroundColor: theme.palette.background.default,
  },
  closeBtn: {
    color: "#ffffff",
    padding: 4,
  },
  accordion: {
    marginBottom: 8,
    borderRadius: 8,
    overflow: "hidden",
    "&::before": { display: "none" },
    border: "1px solid rgba(0, 0, 0, 0.1)",
  },
  accordionSummary: {
    backgroundColor: theme.mode === "light" ? "rgba(0, 0, 0, 0.03)" : "rgba(255, 255, 255, 0.05)",
    minHeight: 56,
    "&$expanded": { minHeight: 56 },
  },
  expanded: {},
  accIcon: {
    color: theme.palette.primary.main,
    fontSize: 22,
    marginRight: 10,
  },
  accTitle: {
    fontWeight: 600,
    fontSize: "0.98rem",
    display: "flex",
    alignItems: "center",
  },
  accDetails: {
    padding: "12px 20px 16px",
    display: "block",
  },
  paragraph: {
    marginBottom: 10,
    fontSize: "0.9rem",
    lineHeight: 1.6,
  },
  introBox: {
    backgroundColor: theme.mode === "light" ? "rgba(0, 122, 255, 0.08)" : "rgba(0, 122, 255, 0.12)",
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    borderRadius: 6,
    padding: "12px 16px",
    marginBottom: 16,
  },
  list: {
    paddingLeft: "22px",
    marginBottom: 12,
    "& li": {
      fontSize: "0.9rem",
      lineHeight: 1.55,
      marginBottom: 6,
    },
  },
  ruleBox: {
    backgroundColor: theme.mode === "light" ? "rgba(255, 193, 7, 0.12)" : "rgba(255, 193, 7, 0.08)",
    borderLeft: "4px solid #ffc107",
    borderRadius: 6,
    padding: "10px 14px",
    marginTop: 8,
    marginBottom: 8,
    "& .ruleTitle": {
      fontWeight: 700,
      fontSize: "0.85rem",
      color: "#b8860b",
      marginBottom: 4,
    },
    "& p": {
      fontSize: "0.85rem",
      margin: 0,
      lineHeight: 1.5,
    },
  },
  sectionLabel: {
    fontWeight: 700,
    fontSize: "0.85rem",
    color: theme.palette.primary.main,
    marginTop: 10,
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
}));

const Regra = ({ titulo, children }) => {
  const classes = useStyles();
  return (
    <Box className={classes.ruleBox}>
      <div className="ruleTitle">⚠️ Atenção — {titulo}</div>
      <p>{children}</p>
    </Box>
  );
};

const TutorialCampanhas = ({ open, onClose }) => {
  const classes = useStyles();
  const [expandido, setExpandido] = useState("visao-geral");

  const handleChange = (panel) => (event, isExpanded) => {
    setExpandido(isExpanded ? panel : false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby="tutorial-campanhas-title"
    >
      <DialogTitle id="tutorial-campanhas-title" className={classes.dialogTitle} disableTypography>
        <Typography component="h2" className={classes.dialogTitleText}>
          🎓 Tutorial Detalhado — Tela de Campanhas
        </Typography>
        <IconButton size="small" className={classes.closeBtn} onClick={onClose} aria-label="Fechar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className={classes.dialogContent}>
        <Box className={classes.introBox}>
          <Typography className={classes.paragraph} style={{ marginBottom: 0 }}>
            Este tutorial explica <strong>detalhadamente</strong> como usar a tela de Campanhas, as
            regras de cada funcionalidade e como configurar os disparos. Clique em cada seção abaixo
            para abrir e ver as explicações.
          </Typography>
        </Box>

        {/* 1. VISÃO GERAL */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "visao-geral"}
          onChange={handleChange("visao-geral")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>🏠</span> Visão Geral da Tela
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              Esta é a tela onde você <strong>dispara mensagens em massa</strong> para muitos clientes
              de uma só vez. É ideal para fazer promoções, avisos, lembretes, confirmações de
              agendamento — qualquer mensagem que precise ir para várias pessoas ao mesmo tempo.
            </Typography>
            <Typography className={classes.paragraph}>
              A tela é dividida em três partes:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Topo:</strong> título com a quantidade de campanhas, campo de busca e botão "Nova Campanha".</li>
              <li><strong>Barra de filtros:</strong> filtros por status, recorrência e datas.</li>
              <li><strong>Tabela:</strong> a lista com todas as campanhas, mostrando nome, status, agendamento e botões de ação.</li>
            </ul>

            <Regra titulo="Só aparece para quem tem permissão">
              O menu de Campanhas só aparece para usuários que têm a permissão liberada no cadastro.
              Além disso, a empresa precisa ter o recurso de campanhas ativo no plano. Se você não vê
              o menu, peça para o administrador verificar.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 2. BUSCA E NOVA CAMPANHA */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "busca-nova"}
          onChange={handleChange("busca-nova")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>🔍</span> Busca e Botão Nova Campanha
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <div className={classes.sectionLabel}>Campo de Busca</div>
            <ul className={classes.list}>
              <li><strong>"Pesquisa":</strong> digite parte do nome da campanha e a lista filtra na hora, enquanto você digita.</li>
              <li>A busca é pelo <strong>nome</strong> da campanha.</li>
              <li>O título lá em cima mostra o <strong>total de campanhas</strong> que existe no sistema, entre parênteses.</li>
            </ul>

            <div className={classes.sectionLabel}>Botão Nova Campanha</div>
            <ul className={classes.list}>
              <li>O botão azul <strong>"Nova Campanha"</strong> abre a tela de cadastro para você criar um disparo novo.</li>
              <li>Lá você escolhe quem vai receber, qual mensagem, quando enviar e por qual número de WhatsApp.</li>
            </ul>
          </AccordionDetails>
        </Accordion>

        {/* 3. FILTROS */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "filtros"}
          onChange={handleChange("filtros")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>🎛️</span> Filtros da Listagem
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              Logo abaixo do topo há uma barra de filtros com quatro opções para você achar campanhas
              específicas:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Filtrar por Status:</strong> mostra só as campanhas de um determinado status (Inativa, Programada, Em Andamento, Cancelada, Finalizada) ou todas.</li>
              <li><strong>Filtrar por Recorrência:</strong> mostra só as campanhas que se repetem (Recorrentes), só as que acontecem uma vez (Únicas), ou todas.</li>
              <li><strong>Data de Agendamento Início:</strong> filtra as campanhas agendadas a partir de uma data.</li>
              <li><strong>Data de Agendamento Fim:</strong> filtra as campanhas agendadas até uma data.</li>
            </ul>
            <Typography className={classes.paragraph}>
              Você pode combinar os filtros. Por exemplo: filtrar por status "Em Andamento" + recorrência
              "Recorrentes" para ver só as campanhas que estão rodando agora e se repetem.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* 4. STATUS DA CAMPANHA */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "status"}
          onChange={handleChange("status")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>🏷️</span> Entendendo os Status da Campanha
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              Cada campanha tem um status que diz em que situação ela está. O status muda sozinho
              conforme a campanha avança:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Inativa (cinza):</strong> a campanha foi criada mas ainda não foi agendada. Não vai disparar ainda.</li>
              <li><strong>Programada (azul):</strong> a campanha tem data e hora marcadas para disparar. O sistema está esperando a hora chegar.</li>
              <li><strong>Em Andamento (laranja):</strong> a campanha está disparando agora. As mensagens estão sendo enviadas uma a uma, com intervalos.</li>
              <li><strong>Cancelada (cinza):</strong> a campanha foi parada manualmente. Pode ser reiniciada se ainda houver contatos que não receberam.</li>
              <li><strong>Finalizada (cinza):</strong> a campanha terminou — todos os contatos foram processados (ou a recorrência chegou ao fim).</li>
            </ul>

            <Regra titulo="O status muda sozinho">
              Você não precisa mudar o status na mão. O sistema coloca como "Programada" quando você
              cria, muda para "Em Andamento" na hora de disparar, e para "Finalizada" quando todos os
              contatos foram processados. Você só mexe no status se quiser <strong>parar</strong> ou{" "}
              <strong>reiniciar</strong> uma campanha.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 5. COLUNAS DA TABELA */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "colunas"}
          onChange={handleChange("colunas")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>📋</span> Colunas da Tabela
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              A tabela mostra várias informações de cada campanha para você acompanhar tudo num olhar:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Nome:</strong> o nome que você deu à campanha. Se tiver um ícone de seta girando ao lado, é uma campanha que se repete (recorrente).</li>
              <li><strong>Status:</strong> um quadradinho colorido com a situação atual (explicado na seção acima).</li>
              <li><strong>Recorrência:</strong> mostra se a campanha se repete (e de quanto em quanto tempo) ou se é única. Também mostra quantas vezes já rodou.</li>
              <li><strong>Lista de Contatos:</strong> o nome da lista de contatos que vai receber, ou a etiqueta (tag) usada, ou "Não definida" se não tem.</li>
              <li><strong>Conexão/WhatsApp:</strong> qual número de WhatsApp vai fazer o disparo, ou "Não definido".</li>
              <li><strong>Criador:</strong> quem criou a campanha. Se não constar, mostra "Não identificado".</li>
              <li><strong>Agendamento:</strong> a data e hora marcadas para o disparo, ou "Sem agendamento".</li>
              <li><strong>Próxima Execução:</strong> quando a campanha vai rodar de novo (para as recorrentes). Se a recorrência acabou, mostra "Recorrência finalizada".</li>
              <li><strong>Concluída:</strong> quando a campanha terminou, ou "Não concluída" se ainda está em andamento.</li>
              <li><strong>Ações:</strong> os botões para gerenciar a campanha (explicados na próxima seção).</li>
            </ul>
          </AccordionDetails>
        </Accordion>

        {/* 6. BOTÕES DE AÇÃO */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "botoes-acao"}
          onChange={handleChange("botoes-acao")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>⚙️</span> Botões de Ação de Cada Campanha
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              No final de cada linha da tabela, existem botões para gerenciar a campanha. Nem todos
              aparecem sempre — depende do status:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Parar Campanha (ícone de pausa):</strong> só aparece quando a campanha está "Em Andamento". Pára o disparo imediatamente. O que já estava sendo enviado pode terminar, mas nada novo é enviado.</li>
              <li><strong>Reiniciar Campanha (ícone de play):</strong> só aparece quando a campanha está "Cancelada". Recomeça o disparo de onde parou — os contatos que já receberam não recebem de novo, só os que faltam.</li>
              <li><strong>Interromper Recorrência (ícone de quadrado vermelho):</strong> só aparece em campanhas que se repetem e não estão finalizadas. Pára as repetições futuras, mas a execução atual continua e termina normalmente.</li>
              <li><strong>Relatório (ícone de documento):</strong> aparece sempre. Abre a tela de relatório detalhado da campanha, onde você vê quem recebeu, quem não recebeu, quantas falharam, etc.</li>
              <li><strong>Editar (ícone de lápis):</strong> aparece sempre. Abre a campanha para alterar os dados (o que pode ser mudado depende do status — explicado mais abaixo).</li>
              <li><strong>Excluir (ícone de lixeira):</strong> aparece sempre. Pergunta se você tem certeza e depois apaga a campanha. <strong>Não dá para desfazer.</strong></li>
            </ul>

            <Regra titulo="Parar é diferente de interromper recorrência">
              <strong>Parar Campanha</strong> pára o disparo que está acontecendo agora.{" "}
              <strong>Interromper Recorrência</strong> deixa a execução atual acabar sozinha, mas
              impede que a campanha rode de novo no futuro. São coisas diferentes para situações
              diferentes.
            </Regra>

            <Regra titulo="Reiniciar só manda para quem faltou">
              Ao reiniciar uma campanha cancelada, o sistema <strong>só envia para os contatos que
              ainda não receberam</strong>. Quem já recebeu a mensagem não recebe de novo. Isso evita
              duplicidade.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 7. CRIAR / EDITAR CAMPANHA */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "criar-editar"}
          onChange={handleChange("criar-editar")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>➕</span> Criar ou Editar uma Campanha
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              Ao clicar em "Nova Campanha" ou no botão de editar, abre uma janela grande com o
              formulário. Os campos são:
            </Typography>

            <div className={classes.sectionLabel}>Dados principais</div>
            <ul className={classes.list}>
              <li><strong>Nome:</strong> o nome da campanha (ex: "Promoção de Janeiro"). Obrigatório, entre 2 e 50 letras.</li>
              <li><strong>Lista de Contato:</strong> escolha de qual lista de contatos vão sair os destinatários. Você também pode escolher "Nenhuma" e usar uma etiqueta (tag) no lugar.</li>
              <li><strong>Tags:</strong> em vez de uma lista, você pode disparar para todos os contatos que tiverem uma determinada etiqueta. Basta escolher a etiqueta. O sistema mostra quantos contatos cada etiqueta tem.</li>
              <li><strong>Conexão / WhatsApp:</strong> qual número de WhatsApp vai fazer o disparo. Obrigatório escolher.</li>
              <li><strong>Agendamento:</strong> a data e a hora que o disparo vai começar. Precisa ser no mínimo 5 minutos daqui para frente.</li>
            </ul>

            <Regra titulo="Lista de contatos OU etiqueta (tag)">
              Você escolhe de onde vêm os destinatários: ou de uma <strong>lista de contatos</strong>{" "}
              (que você cadastrou antes, importando do Excel) ou de uma <strong>etiqueta (tag)</strong>{" "}
              (todos os contatos que tiverem aquela etiqueta). Não dá para usar os dois ao mesmo tempo.
            </Regra>

            <Regra titulo="Agendamento mínimo de 5 minutos">
              A campanha precisa ser agendada com pelo menos <strong>5 minutos de antecedência</strong>.
              Não dá para agendar para "agora". Isso é para dar tempo do sistema se preparar.
            </Regra>

            <div className={classes.sectionLabel}>Template do WhatsApp (só para números oficiais)</div>
            <Typography className={classes.paragraph}>
              Se a conexão escolhida for da <strong>API Oficial do WhatsApp</strong>, aparece um
              botão extra chamado <strong>"Selecionar Template da Meta"</strong>. Ele deixa você
              escolher um modelo de mensagem já aprovado pelo WhatsApp. Quando você escolhe um
              template, os campos de mensagem livre ficam bloqueados, porque o WhatsApp exige que o
              texto seja exatamente o do modelo aprovado.
            </Typography>

            <Regra titulo="Template precisa estar aprovado">
              Se você tentar criar uma campanha usando um template que ainda está pendente ou foi
              rejeitado, o sistema <strong>não deixa</strong>. Só templates com status "Aprovado"
              podem ser usados em campanhas.
            </Regra>

            <div className={classes.sectionLabel}>Abrir ticket após o envio</div>
            <Typography className={classes.paragraph}>
              Você pode configurar a campanha para, depois que enviar a mensagem, <strong>abrir uma
              conversa (ticket)</strong> na tela de Atendimentos. Assim, quando o cliente responder,
              a conversa já aparece pronta para um atendente. Para isso você escolhe:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Abrir ticket:</strong> Habilitado ou não.</li>
              <li><strong>Usuário:</strong> para qual atendente a conversa vai (obrigatório se o ticket estiver habilitado).</li>
              <li><strong>Fila:</strong> para qual setor a conversa vai (obrigatório se o ticket estiver habilitado).</li>
              <li><strong>Status do Ticket:</strong> como a conversa vai começar — Fechado, Pendente ou Aberto.</li>
            </ul>

            <div className={classes.sectionLabel}>Mensagens (variações)</div>
            <Typography className={classes.paragraph}>
              Você pode escrever <strong>até 5 mensagens diferentes</strong> para a mesma campanha.
              O sistema sorteia uma dessas mensagens para cada contato. Isso ajuda a parecer mais
              natural e evita que o WhatsApp ache que é spam (por causa de mensagens idênticas).
            </Typography>
            <ul className={classes.list}>
              <li>Use as abas "Msg. 1", "Msg. 2", etc. para escrever cada variação.</li>
              <li>Você pode usar <strong>variáveis</strong> no texto, como <code>{`{nome}`}</code>, <code>{`{numero}`}</code>, <code>{`{email}`}</code>, que são trocadas pelos dados de cada cliente na hora de enviar.</li>
            </ul>

            <div className={classes.sectionLabel}>Anexar arquivo</div>
            <ul className={classes.list}>
              <li>Você pode anexar uma imagem, documento ou áudio para ir junto com a mensagem.</li>
              <li>Se já tem um arquivo anexado, aparece o nome dele e um botão para remover.</li>
            </ul>
          </AccordionDetails>
        </Accordion>

        {/* 8. RECORRÊNCIA */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "recorrencia"}
          onChange={handleChange("recorrencia")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>🔁</span> Campanhas que se Repetem (Recorrência)
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              Dentro do formulário da campanha tem uma parte chamada <strong>"Configuração de
              Recorrência"</strong>. Ela deixa a campanha se repetir sozinha, sem você precisar criar
              de novo toda vez.
            </Typography>

            <div className={classes.sectionLabel}>Como funciona</div>
            <ul className={classes.list}>
              <li><strong>Habilitar recorrência:</strong> uma chave (liga/desliga). Quando ligada, aparece o resto das opções.</li>
              <li><strong>Tipo de Recorrência:</strong> de quanto em quanto tempo a campanha repete:
                <ul>
                  <li><strong>Por Minuto:</strong> a cada X minutos.</li>
                  <li><strong>Por Hora:</strong> a cada X horas.</li>
                  <li><strong>Diário:</strong> todo dia.</li>
                  <li><strong>Semanal:</strong> em dias da semana específicos (você marca quais — segunda, terça, etc.).</li>
                  <li><strong>Quinzenal:</strong> a cada 2 semanas.</li>
                  <li><strong>Mensal:</strong> em um dia específico do mês (você escolhe o dia, de 1 a 31).</li>
                  <li><strong>Anual:</strong> uma vez por ano.</li>
                </ul>
              </li>
              <li><strong>Intervalo:</strong> o número de minutos, horas ou dias entre cada repetição (ex: a cada 2 horas, a cada 3 dias).</li>
              <li><strong>Dias da Semana:</strong> só aparece se o tipo for Semanal. Marque os dias que a campanha deve rodar.</li>
              <li><strong>Dia do Mês:</strong> só aparece se o tipo for Mensal. Escolha o dia do mês (1 a 31).</li>
              <li><strong>Data Final (opcional):</strong> até quando a campanha vai se repetir. Deixe vazio para repetir para sempre.</li>
              <li><strong>Máximo de Execuções (opcional):</strong> quantas vezes no total a campanha vai rodar. Deixe vazio para repetir sem limite.</li>
              <li><strong>Visualizar Próximas Execuções:</strong> um botão que mostra as próximas 5 vezes que a campanha vai rodar, para você conferir antes de salvar.</li>
            </ul>

            <Regra titulo="Sem data final = repete para sempre">
              Se você não colocar data final nem limite de execuções, a campanha vai se repetir{" "}
              <strong>para sempre</strong>, até você interromper a recorrência manualmente.
            </Regra>

            <Regra titulo="Interromper a recorrência não pára a execução atual">
              Quando você clica em "Interromper Recorrência" (o botão vermelho na tabela), a execução
              que está acontecendo <strong>continua até o fim normalmente</strong>. O que para são as{" "}
              <strong>repetições futuras</strong>. Ou seja, não interrompe no meio, apenas não roda
              mais daqui para frente.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 9. QUANDO PODE EDITAR */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "quando-editar"}
          onChange={handleChange("quando-editar")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>✏️</span> Quando dá para Editar uma Campanha
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              Nem sempre dá para mudar tudo em uma campanha. O que pode ser editado depende do status
              dela:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Inativa:</strong> dá para editar tudo normalmente.</li>
              <li><strong>Programada:</strong> dá para editar <strong>só se faltar mais de 1 hora</strong> para o disparo começar. Se faltar menos de 1 hora, os campos ficam bloqueados para não dar problema.</li>
              <li><strong>Em Andamento:</strong> <strong>não dá para editar</strong> — a campanha já está disparando, não pode mudar nada. Se quiser parar, use o botão "Parar Campanha".</li>
              <li><strong>Cancelada:</strong> os campos ficam bloqueados, mas aparece o botão "Reiniciar Disparos" para você recomeçar.</li>
              <li><strong>Finalizada:</strong> não dá para editar, mas dá para ver tudo (só leitura).</li>
            </ul>

            <Regra titulo="Por que não dá para mudar tudo a qualquer hora">
              Depois que a campanha começa a disparar, as mensagens já estão sendo enviadas. Mudar o
              texto ou a lista no meio do disparo causaria confusão — alguns clientes receberiam uma
              coisa, outros receberiam outra. Por isso o sistema bloqueia as mudanças quando a campanha
              está em andamento ou perto de começar.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 10. CONFIGURAÇÕES GLOBAIS */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "config-global"}
          onChange={handleChange("config-global")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>⚙️</span> Configurações de Campanhas (intervalos)
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              No menu de Campanhas, tem uma opção chamada <strong>"Configurações"</strong>. Ela abre
              uma tela separada onde você ajusta os <strong>intervalos entre as mensagens</strong> de
              todas as campanhas. São três ajustes:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Intervalo Randômico de Disparo:</strong> a pausa entre cada mensagem. Pode ser de 0 (sem intervalo) até 120 segundos. Quanto maior, mais devagar e seguro.</li>
              <li><strong>Intervalo maior após:</strong> depois de quantas mensagens o sistema passa a usar uma pausa maior. Por exemplo, depois de 20 mensagens, dá uma pausona.</li>
              <li><strong>Intervalo de disparo maior:</strong> a pausa maior que entra depois da quantidade acima. Pode ir de 0 a 180 segundos (de 10 em 10).</li>
            </ul>
            <Typography className={classes.paragraph}>
              Depois de ajustar, clique em <strong>Salvar</strong>.
            </Typography>

            <Regra titulo="Por que os intervalos são importantes">
              O WhatsApp não gosta que o mesmo número mande muitas mensagens muito rápido. Se mandar,
              ele pode <strong>bloquear o número</strong> por some tempo. Os intervalos servem justamente
              para <strong>proteger o seu número</strong>, fazendo o disparo parecer mais natural, como
              se uma pessoa estivesse mandando as mensagens uma a uma.
            </Regra>

            <Regra titulo="Exemplo de configuração segura">
              Intervalo de 20 segundos, pausa maior após 20 mensagens, pausa maior de 60 segundos. Ou
              seja: o sistema manda 20 mensagens com 20 segundos entre cada uma, depois dá uma pausa
              de 60 segundos antes de continuar. Isso deixa o disparo mais seguro.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 11. RELATÓRIO */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "relatorio"}
          onChange={handleChange("relatorio")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>📊</span> Relatório da Campanha
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              Ao clicar no botão de <strong>Relatório</strong> (ícone de documento) em uma campanha,
              você abre uma tela detalhada que mostra como o disparo foi. Lá você encontra:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Gráfico de rosca:</strong> mostra a proporção de mensagens pendentes, enviadas, entregues e com falha.</li>
              <li><strong>Cartões de resumo:</strong> total de tentativas, quantas foram enviadas, quantas foram entregues e quantas falharam.</li>
              <li><strong>Barra de progresso:</strong> mostra quantas já foram processadas do total (ex: 150 de 300).</li>
              <li><strong>Tabela detalhada:</strong> lista cada destinatário, mostrando o número, a mensagem, quando foi enviada, quando foi entregue, o status e o erro (se tiver falhado).</li>
              <li><strong>Reenviar falhas:</strong> você pode marcar com um checkbox as mensagens que falharam e clicar em <strong>"Reenviar falhas selecionadas"</strong> para tentar de novo.</li>
              <li><strong>Botão Voltar:</strong> volta para a lista de campanhas.</li>
            </ul>

            <div className={classes.sectionLabel}>Status de cada mensagem</div>
            <ul className={classes.list}>
              <li><strong>Pendente:</strong> ainda não foi enviada, está na fila esperando.</li>
              <li><strong>Enviada à Meta:</strong> a mensagem foi enviada para o WhatsApp, mas ainda não se sabe se chegou no aparelho do cliente.</li>
              <li><strong>Entregue:</strong> chegou no aparelho do cliente. Tudo certo.</li>
              <li><strong>Falhou:</strong> não conseguiu entregar (número inválido, cliente sem WhatsApp, etc.). O motivo aparece na coluna de erro.</li>
            </ul>

            <Regra titulo="O relatório se atualiza sozinho">
              Enquanto a campanha está disparando, o relatório se atualiza <strong>na hora</strong>,
              sem precisar recarregar. Cada mensagem que é enviada, entregue ou falha aparece
              imediatamente na tabela.
            </Regra>

            <Regra titulo="Reenviar só o que falhou">
              Se algumas mensagens falharam (ex: o número não estava no WhatsApp naquele momento), você
              pode selecionar só essas com o checkbox e mandar de novo. Não precisa reenviar a campanha
              inteira — só o que não chegou.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 12. EXEMPLOS PRÁTICOS */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "exemplos"}
          onChange={handleChange("exemplos")
          }
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>💡</span> Exemplos Práticos do Dia a Dia
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <div className={classes.sectionLabel}>Exemplo 1: Promoção única</div>
            <Typography className={classes.paragraph}>
              O marketing quer mandar uma promoção de Black Friday para todos os clientes. Cria uma
              campanha:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Nome:</strong> "Black Friday 2026"</li>
              <li><strong>Lista de Contatos:</strong> escolhe a lista "Clientes Ativos" (importada do Excel antes).</li>
              <li><strong>Conexão:</strong> o número "Vendas".</li>
              <li><strong>Agendamento:</strong> sexta-feira às 9h da manhã.</li>
              <li><strong>Mensagens:</strong> escreve 3 variações diferentes da promoção.</li>
              <li><strong>Anexo:</strong> a imagem do banner da promoção.</li>
            </ul>
            <Typography className={classes.paragraph}>
              Na sexta às 9h, o sistema começa a disparar sozinho, mandando uma das 3 mensagens
              sorteadas para cada cliente, com 20 segundos de intervalo entre cada envio. No relatório,
              o marketing acompanha quantas foram entregues e quantas falharam.
            </Typography>

            <div className={classes.sectionLabel}>Exemplo 2: Lembrete semanal recorrente</div>
            <Typography className={classes.paragraph}>
              A empresa quer mandar um lembrete de pagamento todo sábado para os clientes com boletos
              em aberto. Cria uma campanha:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Nome:</strong> "Lembrete de Pagamento"</li>
              <li><strong>Tags:</strong> escolhe a etiqueta "Boleto em Aberto" (em vez de uma lista).</li>
              <li><strong>Conexão:</strong> o número "Financeiro".</li>
              <li><strong>Recorrência:</strong> ligada. Tipo: Semanal. Dias: só sábado. Sem data final.</li>
              <li><strong>Abrir ticket:</strong> habilitado, para o atendente do setor financeiro, fila "Cobrança", status Pendente.</li>
              <li><strong>Mensagem:</strong> "Olá {`{nome}`}, lembrete que seu boleto vence hoje. Acesse o portal para pagar."</li>
            </ul>
            <Typography className={classes.paragraph}>
              Todo sábado, o sistema dispara sozinho para os clientes com a etiqueta "Boleto em Aberto".
              Quando o cliente responde, a conversa já aparece na tela de Atendimentos para o atendente
              do financeiro, na fila de cobrança. Quando não quiser mais, é só clicar em "Interromper
              Recorrência".
            </Typography>

            <div className={classes.sectionLabel}>Exemplo 3: Confirmação de consulta mensal</div>
            <Typography className={classes.paragraph}>
              Uma clínica quer confirmar as consultas do dia 15 de cada mês. Cria:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Nome:</strong> "Confirmação de Consulta"</li>
              <li><strong>Lista de Contatos:</strong> a lista "Pacientes".</li>
              <li><strong>Recorrência:</strong> ligada. Tipo: Mensal. Dia do mês: 15. Máximo de execuções: 12 (um ano).</li>
              <li><strong>Mensagem:</strong> "Olá {`{nome}`}, passando para confirmar sua consulta. Responda SIM para confirmar."</li>
            </ul>
          </AccordionDetails>
        </Accordion>

        {/* 13. REGRAS GLOBAIS */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "regras-globais"}
          onChange={handleChange("regras-globais")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>🛡️</span> Regras Gerais das Campanhas
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Regra titulo="O disparo não é instantâneo">
              As mensagens não saem todas de uma vez. O sistema manda <strong>uma a uma</strong>, com
              intervalos entre cada, para proteger o número do WhatsApp. Por isso, uma campanha com
              muitos contatos pode levar horas para terminar.
            </Regra>
            <Regra titulo="Proteja o seu número">
              Se você mandar muitas mensagens muito rápido, o WhatsApp pode <strong>bloquear o
              número</strong>. Use intervalos adequados nas configurações e escreva variações
              diferentes da mensagem para não ficar idêntico.
            </Regra>
            <Regra titulo="A lista precisa existir antes">
              Você precisa ter cadastrado a lista de contatos (ou aplicado a etiqueta nos contatos)
              <strong> antes</strong> de criar a campanha. Não dá para importar contatos de dentro da
              campanha.
            </Regra>
            <Regra titulo="A conexão precisa estar conectada">
              O número de WhatsApp escolhido precisa estar <strong>conectado</strong> na hora do
              disparo. Se o número cair, as mensagens vão falhar. Verifique as conexões antes de
              agendar uma campanha grande.
            </Regra>
            <Regra titulo="Excluir é definitivo">
              Ao excluir uma campanha, ela é apagada junto com o histórico de envios. <strong>Não dá
              para desfazer.</strong> Se quiser só pausar, use o botão "Parar Campanha" em vez de
              excluir.
            </Regra>
            <Regra titulo="Atualização em tempo real">
              A lista de campanhas se atualiza sozinha. Quando uma campanha é criada, começa a disparar
              ou termina, a tabela <strong>atualiza na hora</strong>, sem precisar recarregar a
              página.
            </Regra>
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={onClose}
          color="primary"
          variant="contained"
          style={{ textTransform: "none", fontWeight: 600, borderRadius: 8 }}
        >
          Entendi, fechar tutorial
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TutorialCampanhas;
